import React, { useContext, useLayoutEffect } from 'react';
import { styled } from 'styled-components';
import { mediaQueries } from '../styles/mediaQueries';

import Loader from './Loader';
import { IsLoadingPageContext, PageContext, ViewContext } from '../context';
import { useTranslation } from 'react-i18next';
import type { AppPage } from '../types/app';

export interface PageContentProps {
  children: React.ReactNode;
}

interface StyledContentProps {
  $withStickyFooter: boolean;
}

const Content = styled.div<StyledContentProps>`
  padding: 0 1rem;
  display: block;
  margin-bottom: ${(props) => (props.$withStickyFooter ? '0' : '6rem')};

  ${(props) =>
    props.$withStickyFooter &&
    `
    display: flex;
    flex-direction: column;
    flex-grow: 1;    
  `}

  ${mediaQueries.desktopUp} {
    padding: 0;
    margin-bottom: ${(props) => (props.$withStickyFooter ? '0' : '4rem')};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: 272px;
`;

const getLoadingTextKey = (page: AppPage) => {
  if (page === 'timeslots' || page === 'services') {
    return `components.loading.title.${page}`;
  }
  if (page === 'questions') {
    return 'pages.questions.loadingQuestionnaire';
  }
  return 'components.loading.title.generic';
};

const PageContent: React.FC<PageContentProps> = ({ children }) => {
  const { t } = useTranslation();
  const isLoadingPage = useContext(IsLoadingPageContext);
  const page = useContext(PageContext);
  const view = useContext(ViewContext);

  const isInstantLoadingPage =
    page === 'start' || page === 'questions-confirmed';

  useLayoutEffect(() => {
    if (isLoadingPage || isInstantLoadingPage) {
      window.scrollTo(0, 0);
    }
  }, [isLoadingPage, isInstantLoadingPage]);

  const loadingTextKey = getLoadingTextKey(page);
  if (isLoadingPage) {
    return (
      <LoadingContainer>
        <Loader text={t(loadingTextKey)} />
      </LoadingContainer>
    );
  }

  return (
    <Content $withStickyFooter={view === 'alt' || view === 'app'}>
      {children}
    </Content>
  );
};

export default PageContent;
